import { useEffect, useMemo, useRef, useState } from "react";
import { Workspace } from "../components/Shell";
import { ActionStrip } from "../components/ActionStrip";
import { calendarToday, dayKeyOf } from "../lib/days";
import { href } from "../lib/route";
import { shortDate } from "../lib/weekLine";
import { DAY_STATE_WORDS, dayState, finished, loggedSets, nameOr } from "../lib/reviewerSeat";
import { useReviewerClient, useReviewerLinks } from "./useReviewer";

/* THE CLIENT, AS HIS REVIEWER SEES HIM — `#/reviewing/<clientId>` (reviewer seat step 2, 2026-10-10). Two lists and
 * nothing else (no assign, no tasks, no chat, no body numbers — the seat reads none of them):
 *   • FINISHED DAYS — the last four weeks of `reviewer_sessions`, each with his review's state; Review opens the day.
 * His proposed week (8b) is reached from a day's review — the `Edit next week` pencil, where the coach has it (his word
 * 2026-10-10 ~23:55), never from a list of its own here. */
export function ReviewerClient({ clientId }: { clientId: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [clientId]);
  const [today] = useState(() => dayKeyOf(calendarToday()));
  const links = useReviewerLinks();
  const link = links.state.status === "ready" ? links.state.data.find((l) => l.client_id === clientId && l.role === "reviewer" && l.status === "active") : undefined;
  const name = nameOr(link?.other_name);
  const { state } = useReviewerClient(clientId, today);
  const data = state.status === "ready" ? state.data : null;

  const days = useMemo(() => (data === null ? [] : data.sessions.filter(finished)
    .sort((a, b) => (b.scheduled_date ?? "").localeCompare(a.scheduled_date ?? ""))), [data]);
  const workoutOn = (day: string | null) => {
    if (data === null || day === null) return null;
    const ws = data.week.filter((d) => d.scheduled_date === day.slice(0, 10) && d.coach_workouts.kind !== "task");
    return ws.length === 1 ? (ws[0]?.coach_workouts.name ?? null) : null;
  };
  const notReviewer = links.state.status === "ready" && link === undefined;

  return (
    <Workspace>
      <main id="main" className="ours-page roster-page reviewer-page">
        <p className="review-crumb"><a className="review-crumb-link" href={href({ name: "roster" })}>‹ Clients</a></p>
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" ref={heading} tabIndex={-1}>{links.state.status === "ready" ? name : "Reviewing"}</h1>
            <p className="ours-caption">You review. Nothing you write changes their week.</p>
          </div>
        </header>

        {notReviewer && <p className="ht-callout ours-quiet">You are not reviewing this client. <a className="ht-link" href={href({ name: "roster" })}>Back to Clients</a></p>}
        {!notReviewer && state.status === "loading" && <p className="ht-callout ours-quiet" aria-live="polite">Loading their training…</p>}
        {!notReviewer && state.status === "error" && (
          <div role="alert" className="ours-error"><p className="ht-body">Could not load their training.</p><p className="ht-caption">{state.message}</p></div>
        )}

        {!notReviewer && data !== null && (
          <>
            <section className="reviewer-block" aria-labelledby="rv-days">
              <h2 id="rv-days" className="reviewing-title">Finished days</h2>
              {days.length === 0 && <p className="ht-callout ours-quiet">Nothing finished in the last four weeks.</p>}
              {days.length > 0 && (
                <table className="ours-rows roster-rows reviewer-rows">
                  <caption className="ht-visually-hidden">Finished days, newest first</caption>
                  <tbody>
                    {days.map((s) => {
                      const st = dayState(data.reviews.find((r) => r.session_id === s.id), s.scheduled_date, today);
                      return (
                        <tr key={s.id} className="ours-row">
                          <th scope="row" className="reviewer-date"><a className="ours-name" href={href({ name: "reviewingDay", clientId, sessionId: s.id })}>{s.scheduled_date === null ? "—" : shortDate(s.scheduled_date.slice(0, 10))}</a></th>
                          <td className="reviewer-what">{workoutOn(s.scheduled_date) ?? "Workout"}</td>
                          <td className="ours-num reviewer-sets">{loggedSets(s)} {loggedSets(s) === 1 ? "set" : "sets"}</td>
                          <td className="reviewer-state"><span className={`queue-chip is-${st === "sent" ? "ok" : "plain"}`}>{DAY_STATE_WORDS[st]}</span></td>
                          <td className="ours-actions">
                            <ActionStrip label={`Actions for ${shortDate((s.scheduled_date ?? "").slice(0, 10))}`} items={[
                              { key: "review", label: st === "sent" ? "Open" : "Review", href: href({ name: "reviewingDay", clientId, sessionId: s.id }) },
                            ]} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>

          </>
        )}
      </main>
    </Workspace>
  );
}
